import { Currency, Insurance } from '@core/constants/data-types.constants';
import { LiteResponsesWithLimits } from '@core/services/lite.service';
import { OfferingProductConfiguration } from './coinscrap/offering-product-definition.model';
import { CurrencyAmount, DummyCurrencyAmount } from './currency-amount.model';
import { CurrencyExchange, convert } from './currency-exchange';
import { Increase } from './increase.model';
import { InvestmentData } from './investment.model';
import { Limits } from './limits.model';
import { LiteCoverageData, LiteExtraInformation, LiteResponse, LiteTableData } from './lite/lite.model';
import { ProductCartStatus } from './product-cart-status.model';
import { ProductCoverage } from './product-coverage.model';
import { ProductLabels } from './product-labels.model';
import { getProductAvailableTemporalities } from './temporality.model';

export type ProductQuality = 'none' | 'low' | 'medium' | 'high';

export type ProductType = 'normal' | 'investment';

export type Product = NormalProduct | InvestmentProduct;

export interface IProduct {
  id: string;
  liteId: string;
  type: ProductType;
  labels: ProductLabels;
  allowedCurrencies: Currency[];
  selectedCurrency: Currency;
  temporalityConditions: ProductTemporalityCondition[];
  selectedTemporality?: string;
  coverages: ProductCoverage[];
  cartStatus: ProductCartStatus;
  insurance: Insurance;
  tableValues?: LiteTableData[];
  tableExtraInformation?: LiteExtraInformation[];
  totalYearlyFee?: CurrencyAmount;
  tableValuesCols?: string[];
  milestone?: ProductMilestone;
  frequencyFee?: CurrencyAmount;
  policyFee?: CurrencyAmount;
  totalFee?: CurrencyAmount;
  projectedInvestment?: InvestmentData;
  allowedIncreases?: Increase[];
  selectedIncrease?: Increase;
}

export interface ProductMilestone {
  userAge: number;
  childAge?: number;
  year: number;
}

/**
 * A 'normal' product, it has an assuredAmount that the user will receive at the
 * end of the temporality by providing a yearly fee.
 */
export interface NormalProduct extends IProduct {
  type: 'normal';
  assuredAmountLimits: Limits;
  feeLimits: Limits;
  currentAssuredAmount: CurrencyAmount;
  currentFee: CurrencyAmount;
  recommendedAssuredAmount?: CurrencyAmount;
  totalFee: CurrencyAmount;
}

/**
 * An 'investment' product is one that has a unique payment and receives an amount
 * at the end of the temporality, it does not behave like a 'normal' product since
 * the fee is a unique payment (not yearly) and is the only field the user can
 * modify, the received amount at the end of the temporality cannot be played with.
 * Also, it does not have rescue values.
 */
export interface InvestmentProduct extends IProduct {
  type: 'investment';
  investedAmountLimits: Limits;
  amountAtEndOfTemporalityLimits: Limits;
  /**
   * This field is the received amount at the end of the temporality, it cannot
   * be modified by the user.
   */
  amountAtEndOfTemporality: CurrencyAmount;
  currentInvestment: CurrencyAmount;
  recommendedAmountAtEndOfTemporality?: CurrencyAmount;
}

export interface ProductTemporalityCondition {
  id: string;
  if: {
    minAge: number;
    maxAge: number;
    currency: Currency;
  };
  then: {
    yearsDuration?: number;
    ageLimit?: number;
    limits: {
      minYearlyAmount: CurrencyAmount;
      maxInsuredAmount: CurrencyAmount;
    };
    amounts: {
      insuredAmount?: CurrencyAmount;
      feeAmount?: CurrencyAmount;
    };
  };
}

export function deepCloneProduct(product: Product): Product {
  // TODO: use a better approach
  return JSON.parse(JSON.stringify(product));
}

export function calculateProductQuality(product: Product, currencyExchange: CurrencyExchange) {
  const qualities: ProductQuality[] = ['low', 'medium'];
  let maxRangeAmount = 0;
  let minRangeAmount = 0;
  let currentAmount = 0;
  switch (product.type) {
    case 'normal':
      maxRangeAmount = convert(
        product.assuredAmountLimits.max || product.recommendedAssuredAmount,
        product.selectedCurrency,
        currencyExchange
      ).amount;
      minRangeAmount = convert(product.assuredAmountLimits.min, product.selectedCurrency, currencyExchange).amount;
      currentAmount = convert(product.currentAssuredAmount, product.selectedCurrency, currencyExchange).amount;
      break;
    case 'investment':
      maxRangeAmount = convert(
        product.amountAtEndOfTemporalityLimits.max || product.recommendedAmountAtEndOfTemporality,
        product.selectedCurrency,
        currencyExchange
      ).amount;
      minRangeAmount = convert(product.amountAtEndOfTemporalityLimits.min, product.selectedCurrency, currencyExchange)
        .amount;
      currentAmount = convert(product.amountAtEndOfTemporality, product.selectedCurrency, currencyExchange).amount;
      break;
  }

  const amountRange = maxRangeAmount - minRangeAmount;
  const amountValueInRange = currentAmount - minRangeAmount;
  if(amountValueInRange < amountRange * 0.20) {
    return 'low'
  }
  if(amountValueInRange > amountRange * 0.20 && amountValueInRange < amountRange * 0.80) {
    return 'medium'
  }
  if(amountValueInRange > amountRange * 0.80) {
    return 'high'
  }
  return 'low';
}

export function updateProductWithProductConfig(product: Product, productConfig: Product) {
  product.labels = productConfig.labels;
  product.liteId = productConfig.liteId;
  product.type = productConfig.type;
  product.temporalityConditions = productConfig.temporalityConditions;
  product.coverages = productConfig.coverages;
  switch (product.type) {
    case 'normal':
      const normalProductConfig = productConfig as NormalProduct;
      product.assuredAmountLimits = normalProductConfig.assuredAmountLimits;
      product.feeLimits = normalProductConfig.feeLimits;
      break;
    case 'investment':
      const investmentProductConfig = productConfig as InvestmentProduct;
      product.investedAmountLimits = investmentProductConfig.investedAmountLimits;
      break;
  }
}

export function adjustProductDataToMatchTemporality(product: Product, age: number, currencyExchange: CurrencyExchange) {
  if (!product.selectedTemporality) {
    const temporalities = getProductAvailableTemporalities(product, product.selectedCurrency, age);
    if (temporalities.length > 0) {
      product.selectedTemporality = temporalities[0].id;
    } else {
      // Check for temporalities for the other currencies then
      const otherCurrencies = product.allowedCurrencies.filter(c => c !== product.selectedCurrency);
      for (const otherCurrency of otherCurrencies) {
        const otherTemporalities = getProductAvailableTemporalities(product, otherCurrency, age);
        if (otherTemporalities.length > 0) {
          product.selectedCurrency = otherCurrency;
          product.selectedTemporality = otherTemporalities[0].id;
          break;
        }
      }
    }
  }
  let productTemporalityConditionSelected = product.temporalityConditions.find(
    t => t.id === product.selectedTemporality
  );
  if (!productTemporalityConditionSelected) {
    productTemporalityConditionSelected = product.temporalityConditions[0];
  }
  applyProductTemporalityConditionLimits(product, productTemporalityConditionSelected);
}

// Apply temporality limits to the product
export function applyProductTemporalityConditionLimits(product: Product, temporality: ProductTemporalityCondition) {
  const limits = temporality.then.limits;
  if (product.type === 'normal') {
    product.assuredAmountLimits = {
      min: { amount: 0, currency: limits.maxInsuredAmount.currency },
      max: limits.maxInsuredAmount
    };
    product.feeLimits = {
      min: limits.minYearlyAmount,
      max: { amount: 1000000000, currency: limits.minYearlyAmount.currency }
    };
  }
  if (product.type === 'investment') {
    product.amountAtEndOfTemporalityLimits = {
      min: { amount: 0, currency: limits.minYearlyAmount.currency },
      max: limits.maxInsuredAmount
    };
    product.investedAmountLimits = {
      min: limits.minYearlyAmount,
      max: { amount: 1000000000, currency: limits.minYearlyAmount.currency }
    };
  }
}

export function adjustValuesToCurrency(product: Product, currency: Currency, currencyExchange: CurrencyExchange) {
  product.selectedCurrency = currency;
  switch (product.type) {
    case 'investment':
      product.currentInvestment = convert(product.currentInvestment, product.selectedCurrency, currencyExchange);
      product.amountAtEndOfTemporality = convert(
        product.amountAtEndOfTemporality,
        product.selectedCurrency,
        currencyExchange
      );
      product.amountAtEndOfTemporalityLimits.max = convert(
        product.amountAtEndOfTemporalityLimits.max,
        product.selectedCurrency,
        currencyExchange
      );
      product.investedAmountLimits.min = convert(
        product.investedAmountLimits.min,
        product.selectedCurrency,
        currencyExchange
      );
      // Convert also the other limits that depend on the LITE calculations to at least have the same currency
      product.amountAtEndOfTemporalityLimits.min = convert(
        product.amountAtEndOfTemporalityLimits.min,
        product.selectedCurrency,
        currencyExchange
      );
      product.investedAmountLimits.max = convert(
        product.investedAmountLimits.max,
        product.selectedCurrency,
        currencyExchange
      );
      // Convert also the recommended amount to at least have the same currency
      if (product.recommendedAmountAtEndOfTemporality) {
        product.recommendedAmountAtEndOfTemporality = convert(
          product.recommendedAmountAtEndOfTemporality,
          product.selectedCurrency,
          currencyExchange
        );
      }
      break;
    case 'normal':
      product.currentAssuredAmount = convert(product.currentAssuredAmount, product.selectedCurrency, currencyExchange);
      product.assuredAmountLimits.max = convert(
        product.assuredAmountLimits.max,
        product.selectedCurrency,
        currencyExchange
      );
      product.feeLimits.min = convert(product.feeLimits.min, product.selectedCurrency, currencyExchange);
      // Convert also the other limits that depend on the LITE calculations to at least have the same currency
      product.assuredAmountLimits.min = convert(
        product.assuredAmountLimits.min,
        product.selectedCurrency,
        currencyExchange
      );
      product.feeLimits.max = convert(product.feeLimits.max, product.selectedCurrency, currencyExchange);
      // Convert also the recommended amount to at least have the same currency
      if (product.recommendedAssuredAmount) {
        product.recommendedAssuredAmount = convert(
          product.recommendedAssuredAmount,
          product.selectedCurrency,
          currencyExchange
        );
      }
      product.currentFee = convert(product.currentFee, product.selectedCurrency, currencyExchange);
      if (product.projectedInvestment && product.projectedInvestment.amount) {
        product.projectedInvestment.amount = convert(
          product.projectedInvestment?.amount,
          product.selectedCurrency,
          currencyExchange
        );
      }
      break;
  }
  for (const coverage of product.coverages) {
    if (coverage.currentAmount) {
      coverage.currentAmount = convert(coverage.currentAmount, product.selectedCurrency, currencyExchange);
    }
    if (coverage.currentFee) {
      coverage.currentFee = convert(coverage.currentFee, product.selectedCurrency, currencyExchange);
    }
  }
  for (const temporalityCondition of product.temporalityConditions) {
    temporalityCondition.then.limits.maxInsuredAmount = convert(
      temporalityCondition.then.limits.maxInsuredAmount,
      product.selectedCurrency,
      currencyExchange
    );
    temporalityCondition.then.limits.minYearlyAmount = convert(
      temporalityCondition.then.limits.minYearlyAmount,
      product.selectedCurrency,
      currencyExchange
    );
    if (temporalityCondition.then.amounts.feeAmount) {
      temporalityCondition.then.amounts.feeAmount = convert(
        temporalityCondition.then.amounts.feeAmount,
        product.selectedCurrency,
        currencyExchange
      );
    }
    if (temporalityCondition.then.amounts.insuredAmount) {
      temporalityCondition.then.amounts.insuredAmount = convert(
        temporalityCondition.then.amounts.insuredAmount,
        product.selectedCurrency,
        currencyExchange
      );
    }
  }
}

export function updateProductWithLiteValues(product: Product, liteResponse: LiteResponse) {
  switch (product.type) {
    case 'normal':
      product.currentAssuredAmount = {
        amount: liteResponse.basic.insuredSum,
        currency: product.currentAssuredAmount?.currency || product.selectedCurrency
      };
      product.currentFee = {
        amount: liteResponse.basic.feeAmount,
        currency: product.currentFee?.currency || product.selectedCurrency
      };
      if (liteResponse.total) {
        product.totalFee = {
          amount: liteResponse.total.feeAmount,
          currency: product.totalFee?.currency || product.selectedCurrency
        };
      }
      break;
    case 'investment':
      product.currentInvestment = {
        amount: liteResponse.basic.feeAmount,
        currency: product.currentInvestment?.currency || product.selectedCurrency
      };
      product.amountAtEndOfTemporality = {
        amount: liteResponse.basic.insuredSum,
        currency: product.amountAtEndOfTemporality?.currency || product.selectedCurrency
      };
      break;
  }

  if (liteResponse.tableExtraInformation) {
    product.tableExtraInformation = liteResponse.tableExtraInformation;
  }
  if (liteResponse.tableValuesCols) {
    product.tableValuesCols = liteResponse.tableValuesCols;
  }
  if (liteResponse.tableValues) {
    product.tableValues = liteResponse.tableValues;
  }

  if (liteResponse.totalYearlyFee) {
    product.totalYearlyFee = {
      amount: liteResponse.totalYearlyFee.feeAmount,
      currency: product.totalYearlyFee?.currency || product.selectedCurrency
    };
  } else {
    product.totalYearlyFee = undefined;
  }

  product.coverages.forEach(c => {
    const liteCoverageResponse: LiteCoverageData = liteResponse[c.id];
    if (liteCoverageResponse) {
      c.currentAmount = {
        amount: liteCoverageResponse.insuredSum,
        currency: c.currentAmount?.currency || product.selectedCurrency
      };
      c.currentFee = {
        amount: liteCoverageResponse.feeAmount,
        currency: c.currentFee?.currency || product.selectedCurrency
      };
      if (liteCoverageResponse.amountText) {
        c.amountText = liteCoverageResponse.amountText;
      }
    }
  });
  if (liteResponse.frequencyFee) {
    product.frequencyFee = {
      amount: liteResponse.frequencyFee.feeAmount,
      currency: product.frequencyFee?.currency || product.selectedCurrency
    };
  }
  if (liteResponse.policyFee) {
    product.policyFee = {
      amount: liteResponse.policyFee.feeAmount,
      currency: product.policyFee?.currency || product.selectedCurrency
    };
  }
}

export function updateProductWithLiteValuesAndLimits(product: Product, liteResponses: LiteResponsesWithLimits) {
  updateProductWithLiteValues(product, liteResponses.currentResponse);
  switch (product.type) {
    case 'normal':
      product.assuredAmountLimits = {
        min: {
          amount: liteResponses.amountMinLimitResponse.basic.insuredSum,
          currency: product.assuredAmountLimits?.min.currency || product.selectedCurrency
        },
        max: {
          amount: liteResponses.feeMaxLimitResponse.basic.insuredSum,
          currency: product.assuredAmountLimits?.max.currency || product.selectedCurrency
        }
      };
      product.feeLimits = {
        min: {
          amount: liteResponses.amountMinLimitResponse.basic.feeAmount,
          currency: product.feeLimits?.min.currency || product.selectedCurrency
        },
        max: {
          amount: liteResponses.feeMaxLimitResponse.basic.feeAmount,
          currency: product.feeLimits?.max.currency || product.selectedCurrency
        }
      };
      break;
    case 'investment':
      product.amountAtEndOfTemporalityLimits = {
        min: {
          amount: liteResponses.amountMinLimitResponse.basic.insuredSum,
          currency: product.amountAtEndOfTemporalityLimits?.min.currency || product.selectedCurrency
        },
        max: {
          amount: liteResponses.feeMaxLimitResponse.basic.insuredSum,
          currency: product.amountAtEndOfTemporalityLimits?.max.currency || product.selectedCurrency
        }
      };
      product.investedAmountLimits = {
        min: {
          amount: liteResponses.amountMinLimitResponse.basic.feeAmount,
          currency: product.investedAmountLimits?.min.currency || product.selectedCurrency
        },
        max: {
          amount: liteResponses.feeMaxLimitResponse.basic.feeAmount,
          currency: product.investedAmountLimits?.max.currency || product.selectedCurrency
        }
      };
      break;
  }
}

export function getProductTemporalityConditions(
  productConfigurations?: OfferingProductConfiguration[]
): ProductTemporalityCondition[] {
  const productConditionalParams: ProductTemporalityCondition[] = [];
  productConfigurations?.forEach((c, index) => {
    if (c.identifier && c.value) {
      const amounts: { feeAmount?: CurrencyAmount; insuredAmount?: CurrencyAmount } = {};
      if (c.params?.feeAmount) {
        amounts.feeAmount = {
          amount: c.params.feeAmount.value,
          currency: c.params.feeAmount.currency
        };
      }
      if (c.params?.insuredAmount) {
        amounts.insuredAmount = {
          amount: c.params.insuredAmount.value,
          currency: c.params.insuredAmount.currency
        };
      }
      productConditionalParams.push({
        id: c.identifier,
        if: {
          currency: c.value.currency,
          maxAge: c.value.maxAge,
          minAge: c.value.minAge
        },
        then: {
          yearsDuration: c.value.yearsDuration,
          ageLimit: c.value.ageLimit,
          limits: {
            minYearlyAmount: {
              amount: c.value.minYearlyAmount.value,
              currency: c.value.minYearlyAmount.currency
            },
            maxInsuredAmount: {
              amount: c.value.maxInsuredAmount.value,
              currency: c.value.maxInsuredAmount.currency
            }
          },
          amounts
        }
      });
    }
  });
  return productConditionalParams;
}

export function calculateCoveragesFee(product: Product) {
  if (!product) {
    return 0;
  }
  let coveragesFee = 0;
  product.coverages
    ?.filter(c => c.active)
    .forEach(c => {
      coveragesFee += c.currentFee?.amount || 0;
    });
  return coveragesFee;
}

export function calculatePolicyFeeSurcharge(
  product: Product,
  currencyExchange: CurrencyExchange,
  policyFeeSurcharge?: CurrencyAmount
) {
  if (product && policyFeeSurcharge) {
    return convert(policyFeeSurcharge, product.selectedCurrency, currencyExchange).amount;
  }
  return 0;
}

export function calculateTotalFee(product: NormalProduct) {
  let totalFee = calculateCoveragesFee(product);
  if (product?.type === 'normal') {
    totalFee += product.currentFee.amount;
  }
  return totalFee;
}

export function calculateTotalInvestment(product: InvestmentProduct, insuredAge: number) {
  const totalCoveragesFee = calculateCoveragesFee(product);
  if (product?.type === 'investment') {
    const selectedTemporality = getProductAvailableTemporalities(product, product.selectedCurrency, insuredAge).find(
      t => t.id === product.selectedTemporality
    );
    if (selectedTemporality) {
      const investmentTemporality =
        selectedTemporality.type === 'duration' ? selectedTemporality.value : selectedTemporality.value - insuredAge;
      return product.currentInvestment.amount + totalCoveragesFee * investmentTemporality;
    }
  }
  return 0;
}

export const DummyProduct: Product = {
  id: '',
  liteId: '',
  type: 'normal',
  insurance: 'DEATH',
  labels: {
    name: '',
    description: '',
    cardDescription: '',
    cardSubtitle: '',
    cardUpperLabel: ''
  },
  allowedCurrencies: ['MXN', 'USD', 'MXV'],
  selectedCurrency: 'MXN',
  temporalityConditions: [],
  coverages: [],
  assuredAmountLimits: {
    min: DummyCurrencyAmount,
    max: DummyCurrencyAmount
  },
  feeLimits: {
    min: DummyCurrencyAmount,
    max: DummyCurrencyAmount
  },
  currentAssuredAmount: DummyCurrencyAmount,
  currentFee: DummyCurrencyAmount,
  totalFee: DummyCurrencyAmount,
  cartStatus: {}
};
